import { ArrowRight, Home, KeyRound } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * ForgotPassword page - redirects to login with Clerk's built-in password reset
 *
 * Note: Password reset is handled by Clerk's SignIn component.
 * This page provides guidance to users on how to use that flow.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link
            to="/login"
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowRight className="mr-2 h-5 w-5 rotate-180" />
            Back to Login
          </Link>
          <Link
            to="/"
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Home className="mr-2 h-5 w-5" />
            Home
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-4 min-h-[calc(100vh-57px)]">
        {/* Background gradient effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

        <Card className="w-full max-w-md animate-fade-in relative">
          <CardHeader className="text-center space-y-4">
            {/* Logo */}
            <div className="flex justify-center">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-gradient-gold">ErgShoot</CardTitle>
              <CardDescription className="mt-2">Reset your password</CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
              <KeyRound className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
              <div className="space-y-2 text-sm">
                <p className="font-medium text-foreground">How to reset your password:</p>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Go to the login page</li>
                  <li>Enter your email address</li>
                  <li>
                    Click <span className="text-primary font-medium">"Forgot password?"</span>
                  </li>
                  <li>Check your email for a reset code</li>
                  <li>Enter the code and set a new password</li>
                </ol>
              </div>
            </div>

            {/* CTA Button */}
            <Button className="w-full" size="lg" onClick={() => navigate('/login')}>
              Go to Login Page
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            {/* Help text */}
            <p className="text-center text-sm text-muted-foreground">
              The password reset is handled securely through our authentication provider.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
